/**
 * DataTable — generic TanStack Table v8 wrapper (UX-Inventar component inventory).
 * Sortable headers, optional summary footer row, optional row-click (→ Peek later).
 */
import { useState, type ReactNode } from 'react';
import {
  flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/Table';
import { cn } from '../../lib/cn';

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  /** Cells for the summary (Summe) footer row, e.g. <><TableCell>Summe</TableCell>… */
  summary?: ReactNode;
  onRowClick?: (row: T) => void;
  /**
   * Opt-in row selection (STEP 3 — the Aufgaben bulk-action flow). When set, a
   * leading checkbox column is prepended (header = select-all over the CURRENT
   * data, cells = per-row). Checkbox clicks stop propagation so they never
   * trigger `onRowClick`. Every other consumer is unchanged (no `selectable`).
   */
  selectable?: boolean;
  /** Stable id for a row (required when `selectable`). */
  getRowId?: (row: T) => string;
  /** The currently-selected ids (controlled by the host). */
  selectedIds?: ReadonlySet<string>;
  /** Toggle one row's selection. */
  onToggleRow?: (id: string) => void;
  /** Toggle all CURRENT rows (select-all header): `next` = the desired checked state. */
  onToggleAll?: (next: boolean) => void;
  /**
   * MASTER-DETAIL: the id of the row the rest of the screen is currently showing
   * (needs `getRowId`). It gets the active surface plus `aria-current="page"` — distinct
   * from `selectedIds`, which is the bulk-action checkbox selection. A list that drives a
   * detail pane has to say WHICH row the pane belongs to.
   */
  activeRowId?: string;
}

export function DataTable<T>({
  columns,
  data,
  summary,
  onRowClick,
  selectable = false,
  getRowId,
  selectedIds,
  onToggleRow,
  onToggleAll,
  activeRowId,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const useSelection = selectable && !!getRowId;
  const allSelected =
    useSelection && data.length > 0 && data.every((r) => selectedIds?.has(getRowId!(r)));
  const someSelected = useSelection && !allSelected && data.some((r) => selectedIds?.has(getRowId!(r)));

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="bg-secondary hover:bg-transparent">
              {useSelection && (
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Alle auswählen"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={(e) => onToggleAll?.(e.target.checked)}
                    className="size-4 align-middle accent-[hsl(var(--primary))]"
                  />
                </TableHead>
              )}
              {hg.headers.map((h) => {
                const sortable = h.column.getCanSort();
                const sorted = h.column.getIsSorted();
                return (
                  <TableHead
                    key={h.id}
                    onClick={sortable ? h.column.getToggleSortingHandler() : undefined}
                    className={cn(sortable && 'cursor-pointer select-none hover:text-foreground')}
                  >
                    <span className="inline-flex items-center gap-1">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {sortable &&
                        (sorted === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : sorted === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-30" />
                        ))}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const rowId = getRowId ? getRowId(row.original) : undefined;
            const isSelected = useSelection && rowId !== undefined && !!selectedIds?.has(rowId);
            const isActive = rowId !== undefined && rowId === activeRowId;
            return (
            <TableRow
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                onRowClick && 'cursor-pointer',
                isSelected && 'bg-secondary/60',
                isActive && 'bg-accent',
              )}
            >
              {useSelection && (
                <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label="Zeile auswählen"
                    checked={isSelected}
                    onChange={() => onToggleRow?.(rowId!)}
                    className="size-4 align-middle accent-[hsl(var(--primary))]"
                  />
                </TableCell>
              )}
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
            );
          })}
        </TableBody>
        {summary && (
          <TableFooter>
            <TableRow className="hover:bg-transparent">{summary}</TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
}
